import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPaperPlane, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './CommentsPanel.css';

/**
 * CommentsPanel - A reusable component for displaying and adding comments
 * 
 * @param {Object} props
 * @param {Array} props.comments - Array of comment objects with structure: { commentId, comment, timestamp, userId, name }
 * @param {Array} props.activeUsers - Array of users for resolving names
 * @param {Object} props.currentUser - Current user object with userId field
 * @param {boolean} props.isVisible - Whether the panel is visible
 * @param {Function} props.onClose - Callback when panel is closed
 * @param {Function} props.onAddComment - Callback when adding a comment (commentText) => Promise
 * @param {boolean} props.isLoading - Optional loading state for add comment operation
 */
const CommentsPanel = ({
  comments = [],
  activeUsers = [],
  currentUser,
  isVisible,
  onClose,
  onAddComment,
  isLoading = false
}) => {
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  if (!isVisible) return null;

  const handleAddComment = async () => {
    if (!newComment.trim() || isAddingComment || !onAddComment) return;
    
    setIsAddingComment(true);
    
    try {
      await onAddComment(newComment.trim());
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleCommentKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleCommentInputChange = (e) => {
    setNewComment(e.target.value);
  };

  return (
    <div className="comments-panel-overlay" onClick={onClose}>
      <div className="comments-panel" onClick={(e) => e.stopPropagation()}>
        <div className="comments-header">
          <span>Comments ({comments.length})</span>
          <button 
            className="close-comments"
            onClick={onClose}
            title="Close comments"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="comments-list">
          {comments && comments.length > 0 ? (
            comments.map((comment, index) => {
              const commentUser = comment.userId === 'SYSTEM'
                ? { name: 'MedPrompt App' }
                : activeUsers.find(u => u.userId === comment.userId);
              const isCurrentUser = comment.userId === currentUser?.userId;
              // Handle timestamp in milliseconds
              const commentDate = new Date(comment.timestamp);
              
              return (
                <div 
                  key={comment.commentId || index} 
                  className={`comment-item ${isCurrentUser ? 'current-user' : 'other-user'}`}
                >
                  <div className="comment-content">
                    <div className="comment-text">{comment.comment}</div>
                    <div className="comment-meta">
                      <span className="comment-author">
                        {commentUser?.name || 'Unknown User'}
                      </span>
                      <span className="comment-time">
                        {commentDate.toLocaleDateString()} {commentDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-comments">No comments yet</div>
          )}
        </div>
        <div className="comment-input-container">
          <textarea
            className="comment-input"
            placeholder="Add a comment..."
            value={newComment}
            onChange={handleCommentInputChange}
            onKeyPress={handleCommentKeyPress}
            disabled={isAddingComment || isLoading}
            rows={2}
          />
          <button 
            className="send-comment-btn"
            onClick={handleAddComment}
            disabled={!newComment.trim() || isAddingComment || isLoading}
            title="Send comment"
          >
            {(isAddingComment || isLoading) ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPaperPlane} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommentsPanel;
